import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { FiCheck, FiEye, FiEyeOff, FiTrash2, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { StyledTooltip } from 'components/InfoTooltip/InfoTooltip';
import { useBulkSelectionStore, useBulkSelectionStoreApi } from 'hooks';
import * as Ui from './BulkSelection.styles';

const BulkSelectionToolbar = ({
  itemUids,
  isActionPending = false,
  onDelete,
  onMakePrivate,
  onPublish,
}) => {
  const { t } = useTranslation('app', { keyPrefix: 'bulkSelection' });
  const store = useBulkSelectionStoreApi();
  const selectedItemUids = useBulkSelectionStore((state) =>
    state.isSelectionMode ? state.selectedItemUids : null,
  );
  const isOpen = selectedItemUids !== null;
  const selectedCount = selectedItemUids?.size ?? 0;
  const allSelected = itemUids.length > 0 && itemUids.every((uid) => selectedItemUids?.has(uid));
  const visibleStateRef = useRef({ selectedCount, allSelected });

  if (isOpen) visibleStateRef.current = { selectedCount, allSelected };

  const visibleSelectedCount = isOpen ? selectedCount : visibleStateRef.current.selectedCount;
  const visibleAllSelected = isOpen ? allSelected : visibleStateRef.current.allSelected;
  const selectAllLabel = visibleAllSelected ? t('deselectAll') : t('selectAll');

  return (
    <Ui.Toolbar $isOpen={isOpen} role="toolbar" aria-label={t('actions')} aria-hidden={!isOpen}>
      <StyledTooltip title={t('close')} placement="top">
        <Ui.Action
          type="button"
          aria-label={t('close')}
          disabled={!isOpen}
          onClick={() => store.getState().exitSelectionMode()}
        >
          <FiX size={22} />
        </Ui.Action>
      </StyledTooltip>
      <StyledTooltip title={selectAllLabel} placement="top">
        <Ui.Action
          type="button"
          role="checkbox"
          aria-checked={visibleAllSelected}
          aria-label={selectAllLabel}
          disabled={!isOpen}
          onClick={() => store.getState().toggleAll(itemUids)}
        >
          <Ui.SelectAllIndicator $selected={visibleAllSelected} aria-hidden="true">
            {visibleAllSelected && <FiCheck size={17} strokeWidth={3} />}
          </Ui.SelectAllIndicator>
        </Ui.Action>
      </StyledTooltip>
      <Ui.SelectedCount aria-live="polite">
        {t('selectedCount', { count: visibleSelectedCount })}
      </Ui.SelectedCount>
      {onMakePrivate && (
        <StyledTooltip title={t('makePrivateSelected')} placement="top">
          <span>
            <Ui.Action
              type="button"
              aria-label={t('makePrivateSelected')}
              disabled={!isOpen || visibleSelectedCount === 0 || isActionPending}
              onClick={onMakePrivate}
            >
              <FiEyeOff size={20} />
            </Ui.Action>
          </span>
        </StyledTooltip>
      )}
      {onPublish && (
        <StyledTooltip title={t('publishSelected')} placement="top">
          <span>
            <Ui.Action
              type="button"
              aria-label={t('publishSelected')}
              disabled={!isOpen || visibleSelectedCount === 0 || isActionPending}
              onClick={onPublish}
            >
              <FiEye size={20} />
            </Ui.Action>
          </span>
        </StyledTooltip>
      )}
      <StyledTooltip title={t('deleteSelected')} placement="top">
        <span>
          <Ui.Action
            type="button"
            $danger
            aria-label={t('deleteSelected')}
            disabled={!isOpen || visibleSelectedCount === 0 || isActionPending}
            onClick={onDelete}
          >
            <FiTrash2 size={20} />
          </Ui.Action>
        </span>
      </StyledTooltip>
    </Ui.Toolbar>
  );
};

BulkSelectionToolbar.propTypes = {
  itemUids: PropTypes.arrayOf(PropTypes.string).isRequired,
  isActionPending: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onMakePrivate: PropTypes.func,
  onPublish: PropTypes.func,
};

export default BulkSelectionToolbar;
